import { request } from "../lib/api-client.ts";
import { ApiError } from "../lib/api-error.ts";
import { API_BASE } from "../lib/api-routes.ts";
import type { operations } from "../lib/api-types.ts";
import { ALFRED_PAGE_MAX } from "../lib/limits.ts";
import { errorMessage } from "../lib/query.ts";

/**
 * Alfred: el buzón de lo que se necesita, que antes era el de sugerencias.
 * Todo lo que el front sabe de sus herramientas vive aquí: sus nombres, su
 * entrada y su salida. Quién lo deja no se manda: lo pone el back con la
 * sesión. Quién es admin, qué puede hacer cada uno y a qué estado se pasa
 * lo decide el back, y llega en el detalle (`can*`, `allowedTransitions`).
 *
 * Los tipos salen de los generados (`lib/api-types.ts`, `pnpm api:types`).
 */

/** Los nombres de las herramientas en el catálogo del API. */
export const ALFRED_TOOLS = {
  create: "create_alfred_ticket",
  update: "update_alfred_ticket",
  cancel: "cancel_alfred_ticket",
  remove: "delete_alfred_ticket",
  comment: "comment_alfred_ticket",
  changeStatus: "change_alfred_ticket_status",
  confirm: "confirm_alfred_ticket",
  reopen: "reopen_alfred_ticket",
  setCc: "set_alfred_ticket_cc",
  users: "search_alfred_users",
  list: "list_alfred_tickets",
  get: "get_alfred_ticket",
} as const;

/** La raíz de las queries de Alfred: crear, comentar o cambiar algo las vuelve a pedir. */
export const ALFRED_KEY = ["alfred"] as const;

/* ------------------------------------------------------------------------ */
/* Los tipos: los generados del API (`pnpm api:types`)                       */
/* ------------------------------------------------------------------------ */

type Json<R> = R extends { content: { "application/json": { data: infer D } } }
  ? D
  : never;

/** Lo que devuelve una query (200) o un command (201). */
type Data<K extends keyof operations> = operations[K] extends {
  responses: infer R;
}
  ? R extends { 200: infer Ok }
    ? Json<Ok>
    : R extends { 201: infer Created }
      ? Json<Created>
      : never
  : never;

type Body<K extends keyof operations> = operations[K] extends {
  requestBody: { content: { "application/json": infer B } };
}
  ? B
  : never;

export type AlfredTicketPage = Data<"list_alfred_tickets">;
export type AlfredTicketSummary = AlfredTicketPage["items"][number];
/** Los conteos del admin (`null` para los demás). */
export type AlfredCounts = NonNullable<AlfredTicketPage["counts"]>;
export type AlfredTicket = Data<"get_alfred_ticket">;
/** Una persona: lo mismo en el autor, el hilo y la copia. */
export type AlfredCcPerson = AlfredTicket["cc"][number];
export type AlfredComment = AlfredTicket["comments"][number];
/** Cada cambio de estado: el pedido nace en «Nuevo» sin entrada propia. */
export type AlfredHistoryEntry = AlfredTicket["history"][number];
export type AlfredAttachment = AlfredTicket["attachments"][number];
export type AlfredStatus = AlfredTicket["status"];
/** Quien pide o comenta: nombre y correo pueden no venir. */
export type AlfredPerson = AlfredTicketSummary["author"];
/** Quien cambió el estado: sin correo. */
export type AlfredActor = AlfredHistoryEntry["by"];
export type CreateTicketInput = Body<"create_alfred_ticket">;
export type Created = Data<"create_alfred_ticket">;

/**
 * Los estados, en el orden en que avanza un pedido: el del filtro de la
 * lista. `satisfies` y las etiquetas de abajo, `Record`, hacen que un estado
 * nuevo en el API no compile hasta que se diga aquí.
 */
export const ALFRED_STATUSES = [
  "new",
  "needs_info",
  "in_progress",
  "delivered",
  "closed",
  "cancelled",
] as const satisfies readonly AlfredStatus[];

/**
 * Con las palabras del equipo, las mismas del API. Donde el API manda el
 * suyo (`statusLabel` de la lista y del detalle) se usa el suyo; estas son
 * para lo que llega sin él: el filtro, los estados a los que se puede pasar
 * y el historial.
 */
export const ALFRED_STATUS_LABELS: Record<AlfredStatus, string> = {
  new: "Nuevo",
  needs_info: "Necesita información",
  in_progress: "En progreso",
  delivered: "Entregado",
  closed: "Cerrado",
  cancelled: "Anulado",
};

/** Un estado que el front no conoce se dice tal cual: no se esconde. */
export const statusLabel = (status: string): string =>
  (ALFRED_STATUS_LABELS as Partial<Record<string, string>>)[status] ?? status;

/** Cómo se nombra a alguien: el nombre, o el correo si no lo hay. */
export const personName = (person: {
  readonly name: string | null;
  readonly email?: string | null;
}): string => person.name || person.email || "—";

export const ALFRED_TEXTS = {
  section: "Alfred",
  button: "Cuéntale a Alfred",
  command: "Cuéntale a Alfred lo que necesitas",
  title: "Cuéntale a Alfred lo que necesitas",
  description:
    "Una idea, algo que falta o algo que no funciona. Le avisamos por correo a quien atiende Alfred, y su estado lo sigues en Alfred, en el menú.",
  who: "Quién",
  subject: "Título",
  body: "Descripción",
  send: "Enviar",
  sending: "Enviando…",
  cancel: "Cancelar",
  close: "Cerrar",
  required: (field: string) => `Falta «${field}».`,
  thanks: "Listo: Alfred ya tiene tu pedido.",
  noConnection:
    "No se pudo enviar: no hay conexión con el servidor. Lo escrito sigue aquí.",
  /* Los adjuntos. */
  attachments: "Adjuntos",
  attach: "Adjuntar",
  attachHint: (paste: string) => `o pega una captura o un archivo con ${paste}`,
  remove: (name: string) => `Quitar ${name}`,
  uploading: "Subiendo…",
  uploaded: "Subido",
  uploadFailed: "No se subió",
  retryUploads: "Reintentar los adjuntos",
  done: "Terminar",
  savedButUploads: (count: number) =>
    count === 1
      ? "Lo escrito quedó guardado, pero un adjunto no se subió. Reintenta o quítalo."
      : `Lo escrito quedó guardado, pero ${String(count)} adjuntos no se subieron. Reintenta o quítalos.`,
} as const;

/** La ruta donde se está, sin el origen, dentro del tope del contrato. */
export const currentPage = (): string =>
  `${window.location.pathname}${window.location.search}`.slice(
    0,
    ALFRED_PAGE_MAX,
  );

const command = <T>(tool: string, input: unknown) =>
  request<T>(`${API_BASE}/commands/${tool}`, {
    method: "POST",
    body: JSON.stringify(input),
    headers: { "Idempotency-Key": crypto.randomUUID() },
  });

const query = <T>(tool: string, params: Record<string, string | undefined>) => {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined) search.set(key, value);
  }
  const suffix = search.size === 0 ? "" : `?${search.toString()}`;
  return request<T>(`${API_BASE}/queries/${tool}${suffix}`);
};

export const createTicket = (input: CreateTicketInput) =>
  command<Created>(ALFRED_TOOLS.create, input);

export const updateTicket = (input: {
  readonly id: string;
  readonly title?: string;
  readonly description?: string;
}) => command<unknown>(ALFRED_TOOLS.update, input);

export const cancelTicket = (input: {
  readonly id: string;
  readonly reason?: string;
}) => command<unknown>(ALFRED_TOOLS.cancel, input);

export const deleteTicket = (input: { readonly id: string }) =>
  command<unknown>(ALFRED_TOOLS.remove, input);

export const commentTicket = (input: {
  readonly ticketId: string;
  readonly body: string;
}) => command<Created>(ALFRED_TOOLS.comment, input);

export const changeTicketStatus = (input: {
  readonly id: string;
  readonly status: AlfredStatus;
  readonly note?: string;
}) => command<unknown>(ALFRED_TOOLS.changeStatus, input);

/** «Me sirvió»: quien pidió cierra el pedido entregado. */
export const confirmTicket = (input: { readonly id: string }) =>
  command<unknown>(ALFRED_TOOLS.confirm, input);

/** «Reabrir»: el comentario es obligatorio y le llega a Alfred. */
export const reopenTicket = (input: {
  readonly id: string;
  readonly comment: string;
}) => command<unknown>(ALFRED_TOOLS.reopen, input);

/** Cambia a quién va en copia: la lista entera de ids. */
export const setTicketCc = (input: {
  readonly id: string;
  readonly ccUserIds: readonly string[];
}) => command<unknown>(ALFRED_TOOLS.setCc, input);

/** Los usuarios a quienes se puede poner en copia, por nombre o correo. */
export const searchCcUsers = (text: string) =>
  query<Data<"search_alfred_users">>(ALFRED_TOOLS.users, { text });

export const listTickets = (params: {
  readonly status?: AlfredStatus | undefined;
  readonly cursor?: string | undefined;
}) => query<AlfredTicketPage>(ALFRED_TOOLS.list, params);

export const getTicket = (id: string) =>
  query<AlfredTicket>(ALFRED_TOOLS.get, { id });

const attachmentsPath = (ticketId: string) =>
  `${API_BASE}/alfred-tickets/${encodeURIComponent(ticketId)}/attachments`;

/**
 * Sube un adjunto al pedido o, con `commentId`, a uno de sus comentarios.
 * Por `request` como todo lo demás: es multipart y `request` sabe no ponerle
 * Content-Type.
 */
export const uploadAttachment = (
  target: { readonly ticketId: string; readonly commentId?: string },
  file: File,
): Promise<AlfredAttachment> => {
  const body = new FormData();
  body.append("file", file);
  if (target.commentId !== undefined)
    body.append("commentId", target.commentId);
  return request<AlfredAttachment>(attachmentsPath(target.ticketId), {
    method: "POST",
    body,
  });
};

export const deleteAttachment = (ticketId: string, attachmentId: string) =>
  request<unknown>(
    `${attachmentsPath(ticketId)}/${encodeURIComponent(attachmentId)}`,
    { method: "DELETE" },
  );

const KB = 1024;
const MB = KB * KB;

/** El peso de un archivo, para leerlo: «820 KB», «2,4 MB». */
export const formatBytes = (bytes: number): string => {
  if (bytes < KB) return `${String(bytes)} B`;
  if (bytes < MB) return `${String(Math.max(1, Math.round(bytes / KB)))} KB`;
  return `${(bytes / MB).toFixed(1).replace(/\.0$/, "").replace(".", ",")} MB`;
};

/** Si un adjunto se ve como miniatura. */
export const isImage = (mimeType: string): boolean =>
  mimeType.startsWith("image/");

/** Lo que se dice de un fallo, en llano: sin el «Failed to fetch» de la red. */
export const alfredErrorOf = (error: unknown): string =>
  error instanceof ApiError ? errorMessage(error) : ALFRED_TEXTS.noConnection;
